/**
 * Training — see Training_Module_Plan.md §3 for the route tables this file implements. It now
 * covers the employee side end to end (the "My Training" dashboard, Task Detail's content player
 * and knowledge check, the webcam consent notice, and quiz attempts), the manager/HR side (library
 * authoring in `trainingTasks`, audience-based bulk assignment and batches, categories, the admin
 * monitor and per-legal-entity policy in `trainingAdmin`), and the Phase 2A Keka-parity layer
 * (certificates, public verification, competencies and learning analytics) — all contract-only per
 * invariant 8: routes here land before the handlers that implement them.
 *
 * `trainingAssignments.get`/`list` moved from purely self-scoped (`routeMeta('learning:read')`) to
 * `routeMeta(null)` plus an in-handler `can()`, now that `scope=own|team|org` exists on `list` — see
 * that router's routes for the reasoning. Every other assignee-scoped route (`start`/`complete`/
 * `reportIssue`/quiz attempts/consent) is unchanged: still self-only, still `learning:read` at the
 * permission layer with the in-handler self-check doing the rest.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  timezoneSchema,
  trainingAssignmentStatusSchema,
  trainingCameraStateSchema,
  trainingContentKindSchema,
  trainingDisplayStatusSchema,
  trainingIssueCategorySchema,
  trainingPauseReasonSchema,
  trainingQuizQuestionTypeSchema,
  trainingSessionEndReasonSchema,
  trainingTaskStatusSchema,
  trainingVerificationStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  TRAINING_LANGUAGES,
  TRAINING_CERTIFICATE_ACCENTS,
  TRAINING_CERTIFICATE_LAYOUTS,
  TRAINING_CERTIFICATE_LOGO_SOURCES,
  TRAINING_CERTIFICATE_ORIENTATIONS,
  TRAINING_CERTIFICATE_VALIDITIES,
  TRAINING_EMBED_PROVIDERS,
  TRAINING_LEARNER_STATUSES,
} from '../enums.js';
import {
  attendanceRegularisationDtoSchema,
  trainingOverrunRegularisationReasonCategorySchema,
} from './attendance.contract.js';
import { queryBooleanSchema } from './chat.contract.js';
import { learningAssignmentSchema } from './learning.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * Every financial-adjacent / bulk-side-effect mutation in this contract requires this header
 * (CLAUDE.md invariant 10 — a retried request must return the original result, never execute
 * twice). Declared locally, the same way `expense.contract.ts` and `leave.contract.ts` each do,
 * rather than centrally: ts-rest only types a `headers` field on a client call when the route
 * itself declares one (`@ts-rest/core`'s `ClientInferRequestBase` resolves `THeaders` to `never`
 * otherwise), so without this on each route, `apps/web`/`apps/mobile` have no typed way to send
 * the header at all.
 */
const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

/** 24-hour wall-clock time, matching `attendance.contract.ts`'s local `wallClockTimeSchema`. Used
 * for `dueTime`/`availableFromTime` — a training deadline is a LOCAL time resolved through
 * `location.timezone` (CLAUDE.md invariant 3), never a UTC instant on the wire. */
const trainingTimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be 24-hour HH:MM');

// ---- Training in several languages ----------------------------------------------------------------
//
// One course, one lesson, one quiz — written once and translated, never duplicated. The row's own
// columns hold the text and video in its `language` (default English); everything else hangs off it
// as a per-language map. Scoring never looks at language: a quiz answer is an option KEY.

export const trainingLanguageSchema = z.enum(TRAINING_LANGUAGES);

/** The languages a course is offered in, its own `language` included — what HR picked in the builder,
 * so a language added but not yet filled in survives a save and reopen. The API normalises it (own
 * language added, duplicates dropped, `TRAINING_LANGUAGES` order). Learners still only get a
 * language in the switcher once the course has some content in it (`availableTrainingLanguages`).
 * Omitted on create → the course's own language alone; omitted on patch → unchanged. */
export const trainingCourseLanguagesSchema = z.array(trainingLanguageSchema).max(TRAINING_LANGUAGES.length);

/** A course's or lesson's title and description in one other language. A missing field falls back to
 * the row's own text. Keyed by language (a missing key = not translated). */
export const trainingTextTranslationSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
});
export const trainingTextTranslationsSchema = z.record(trainingLanguageSchema, trainingTextTranslationSchema);
export type TrainingTextTranslations = z.infer<typeof trainingTextTranslationsSchema>;

/** A quiz question in one other language. `options` maps the option KEY to its translated label, so
 * the correct answer, shuffling and scoring are the same in every language. */
export const trainingQuizQuestionTranslationSchema = z.object({
  prompt: z.string().trim().min(1).max(2000),
  options: z.record(z.string(), z.string().trim().min(1)),
  explanation: z.string().trim().max(1000).optional(),
});
export const trainingQuizQuestionTranslationsSchema = z.record(
  trainingLanguageSchema,
  trainingQuizQuestionTranslationSchema,
);
export type TrainingQuizQuestionTranslations = z.infer<typeof trainingQuizQuestionTranslationsSchema>;

export const trainingTaskSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  allottedMinutes: z.number().int(),
  requiresWebcam: z.boolean(),
  blocksClockOut: z.boolean(),
  /** The first content item's kind, for the card's content-type icon. `null` only if a task was
   * somehow created with no items, which the contract's create route (a later pass) will forbid. */
  contentKind: trainingContentKindSchema.nullable(),
  // ---- Training redesign (course cards) — every field below is additive. ----
  /** Short-lived GET for the uploaded cover; `null` → the client draws the category cover. */
  coverImageUrl: z.string().nullable(),
  /** Lessons in the course (every content item, required or not). */
  lessonCount: z.number().int(),
  /** Sum of the video lessons' durations — the "1h 20m" on a card. Documents/articles add nothing. */
  totalDurationSeconds: z.number().int(),
  // ---- Several languages (additive; absent = English only) ----
  /** The language `title` / `description` are written in. */
  language: trainingLanguageSchema.optional(),
  /** Title and description in the other languages. */
  translations: trainingTextTranslationsSchema.optional(),
  /** The languages the course is offered in, its own included (see `trainingCourseLanguagesSchema`). */
  languages: trainingCourseLanguagesSchema.optional(),
});
export type TrainingTaskSummary = z.infer<typeof trainingTaskSummarySchema>;

// ---- Training redesign: sections, embeds, learner states (docs/plans/training-redesign) ---------

export const trainingEmbedProviderSchema = z.enum(TRAINING_EMBED_PROVIDERS);
export const trainingLearnerStatusSchema = z.enum(TRAINING_LEARNER_STATUSES);
export const trainingDueOverlaySchema = z.enum(['overdue', 'due_soon']).nullable();

/** A named group of lessons ("1. Introduction"). A course with no sections — every course made
 * before the redesign — is shown as one unnamed section; its lessons have `sectionId: null`. */
export const trainingSectionSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  sortOrder: z.number().int(),
});
export type TrainingSection = z.infer<typeof trainingSectionSchema>;

/** A section on create/patch. Lessons point at it by array position (`sectionIndex`), because a
 * brand-new section has no id yet. `id` is echoed back on edit only so a client can keep its keys. */
export const trainingSectionInputSchema = z.object({
  id: uuidSchema.optional(),
  title: z.string().trim().min(1).max(200),
});

/** A competency the course awards on completion (D18), as authored. Level is the 1–5 scale of
 * `Competency.expectedLevelByGrade`. */
export const trainingCourseCompetencyInputSchema = z.object({
  competencyId: uuidSchema,
  level: z.number().int().min(1).max(5),
});
export const trainingCourseCompetencySchema = trainingCourseCompetencyInputSchema.extend({
  competencyName: z.string(),
});

// ---- Task library / authoring (plan §3.1, §3.7) --------------------------------------------------
//
// `learning:manage` (and, for the read routes, `learning.assign:execute` — a manager assigning work
// needs to browse the library without holding authoring rights) callers only. Never surfaced to a
// learner: their view of a task is always through an assignment (`trainingAssignmentDetailSchema`
// above), never this shape.

/** One authored question, INCLUDING `correct` — this shape only ever appears on a `learning:manage`
 * response/request, never on the learner-facing `trainingQuizQuestionSchema` above (D15). */
export const trainingQuizQuestionAuthoringSchema = z.object({
  id: uuidSchema.optional(),
  type: trainingQuizQuestionTypeSchema,
  prompt: z.string().trim().min(1).max(2000),
  options: z.array(z.object({ key: z.string(), label: z.string() })).min(2).max(10),
  correct: z.array(z.string()).min(1),
  points: z.number().positive(),
  explanation: z.string().trim().max(1000).optional(),
  /** The question in other languages; option keys must be among `options[].key` (checked by the API). */
  translations: trainingQuizQuestionTranslationsSchema.optional(),
});

/** What `contentUploadTicket` accepts for `kind: 'video'` — the containers every evergreen browser
 * plays in a bare `<video>` element. QuickTime/MKV are left out on purpose: they upload fine but
 * play in some browsers and not others, which a learner would hit as a "broken video". */
export const TRAINING_VIDEO_UPLOAD_MIME_TYPES = ['video/mp4', 'video/webm'] as const;
/** Mirrors the ticket body's own `sizeBytes` ceiling, so the builder can refuse before asking. */
export const TRAINING_VIDEO_UPLOAD_MAX_BYTES = 500 * 1024 * 1024;

/**
 * The same lesson's video in another language — what saves HR from adding "Policy (English)" and
 * "Policy (Hindi)" as two lessons. The lesson's own video stays the primary; each of these is an
 * extra. At most one per language and never the lesson's own language (the API checks both — a
 * refinement here would stop the union below from being extended).
 */
export const trainingVideoVariantInputSchema = z.discriminatedUnion('kind', [
  z.object({
    language: trainingLanguageSchema,
    kind: z.literal('video_url'),
    url: z.string().url(),
    /** Same rule as the lesson's own `video_url`. */
    durationSeconds: z.number().int().positive().optional(),
  }),
  z.object({
    language: trainingLanguageSchema,
    kind: z.literal('video_upload'),
    storageKey: z.string().min(1),
    mimeType: z.enum(TRAINING_VIDEO_UPLOAD_MIME_TYPES),
    durationSeconds: z.number().int().positive(),
  }),
]);
export type TrainingVideoVariantInput = z.infer<typeof trainingVideoVariantInputSchema>;

/** A language version of a lesson's video as a learner gets it: `url` is the author's link or a
 * short-lived presigned GET — the storage key never reaches a learner. */
export const trainingVideoVariantDetailSchema = z.object({
  language: trainingLanguageSchema,
  kind: z.enum(['video_url', 'video_upload']),
  url: z.string().nullable(),
  mimeType: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
  embedProvider: trainingEmbedProviderSchema.nullable(),
  embedId: z.string().nullable(),
});
export type TrainingVideoVariantDetail = z.infer<typeof trainingVideoVariantDetailSchema>;

/** The author's view of a version: the detail plus the `storageKey` a save sends back unchanged. */
export const trainingVideoVariantAuthoringSchema = trainingVideoVariantDetailSchema.extend({
  storageKey: z.string().nullable(),
});
export type TrainingVideoVariantAuthoring = z.infer<typeof trainingVideoVariantAuthoringSchema>;

/** At most one version per language, the lesson's own included. */
const MAX_TRAINING_VIDEO_VARIANTS = TRAINING_LANGUAGES.length - 1;

/**
 * Discriminated on `kind` — the shape the API validates a content item against on
 * create/patch (plan §3.1). `document`'s and `video_upload`'s `storageKey` comes from
 * `POST /training/tasks/content/upload-ticket` below; the contract never accepts raw file bytes
 * (invariant 9: a route whose request is unsatisfiable by a model gets `ai: 'hidden'`, not a
 * hand-fed base64 body).
 */
/** Fields every lesson kind shares. `sectionIndex` / `description` are the redesign's additions —
 * optional, so a client that predates them still validates. */
const trainingLessonInputBase = z.object({
  title: z.string().trim().min(1).max(200),
  required: z.boolean().default(true),
  /** Position in the body's `sections` array. Omitted → the lesson sits outside any section. */
  sectionIndex: z.number().int().min(0).max(49).optional(),
  /** "About this lesson", shown under the player. */
  description: z.string().trim().max(2000).optional(),
  /** The language this lesson's own title, description and video are in. Omitted → English. */
  language: trainingLanguageSchema.optional(),
  /** Title and description in the other languages. */
  translations: trainingTextTranslationsSchema.optional(),
});

export const trainingContentItemInputSchema = z.discriminatedUnion('kind', [
  trainingLessonInputBase.extend({
    kind: z.literal('video_url'),
    /**
     * https only. Either a direct MP4/WebM file, or a YouTube / Vimeo video link — the server runs
     * `parseTrainingVideoEmbed()` and stores the provider + id, and REJECTS an embed on a
     * strict-mode course (redesign decision 8.3: strict courses need uploaded videos).
     */
    url: z.string().url(),
    /**
     * Omit for a YouTube / Vimeo link and the server reads the length from the provider on save
     * (so a caller with only a link — the AI copilot — can add one). A 400 asks for it when the
     * provider will not say, and always for a direct file link. The lesson editor still sends it.
     */
    durationSeconds: z.number().int().positive().optional(),
    /** The same video in other languages (see `trainingVideoVariantInputSchema`). */
    variants: z.array(trainingVideoVariantInputSchema).max(MAX_TRAINING_VIDEO_VARIANTS).optional(),
  }),
  trainingLessonInputBase.extend({
    kind: z.literal('video_upload'),
    storageKey: z.string().min(1),
    mimeType: z.enum(TRAINING_VIDEO_UPLOAD_MIME_TYPES),
    // Read from the file's own metadata by the builder; progress and completion are measured
    // against it exactly as for `video_url`.
    durationSeconds: z.number().int().positive(),
    variants: z.array(trainingVideoVariantInputSchema).max(MAX_TRAINING_VIDEO_VARIANTS).optional(),
  }),
  trainingLessonInputBase.extend({
    kind: z.literal('document'),
    storageKey: z.string().min(1),
    pageCount: z.number().int().positive(),
  }),
  trainingLessonInputBase.extend({
    kind: z.literal('reading'),
    bodyMarkdown: z.string().min(1),
  }),
  trainingLessonInputBase.extend({
    kind: z.literal('checklist'),
    checklist: z.array(z.object({ key: z.string(), label: z.string() })).min(1),
  }),
  trainingLessonInputBase.extend({
    kind: z.literal('quiz'),
    passMarkPercent: z.number().int().min(0).max(100),
    maxAttempts: z.number().int().positive(),
    timeLimitMinutes: z.number().int().positive().optional(),
    shuffleQuestions: z.boolean().default(true),
    revealAnswers: z.boolean().default(false),
    questions: z.array(trainingQuizQuestionAuthoringSchema).min(1),
  }),
]);
export type TrainingContentItemInput = z.infer<typeof trainingContentItemInputSchema>;

/** A content item as the AUTHOR sees it: every `kind`-specific column, `correct` included on a
 * quiz question. The route only ever populates this for a `learning:manage` caller (plan §3.7:
 * "includes `correct` only for callers holding `learning:manage`"); a caller without it gets
 * `trainingContentItemDetailSchema` instead (e.g. via `preview`). */
export const trainingContentItemAuthoringSchema = z.object({
  id: uuidSchema,
  sortOrder: z.number().int(),
  kind: trainingContentKindSchema,
  title: z.string(),
  required: z.boolean(),
  /** `video_url`: the author's link. `video_upload` on `get`: a short-lived playback GET for the
   * builder's preview — read-only, a save always sends `storageKey`. */
  url: z.string().nullable(),
  storageKey: z.string().nullable(),
  mimeType: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
  pageCount: z.number().int().nullable(),
  bodyMarkdown: z.string().nullable(),
  wordCount: z.number().int().nullable(),
  checklist: z.array(z.object({ key: z.string(), label: z.string() })).nullable(),
  passMarkPercent: z.number().int().nullable(),
  maxAttempts: z.number().int().nullable(),
  timeLimitMinutes: z.number().int().nullable(),
  shuffleQuestions: z.boolean().nullable(),
  revealAnswers: z.boolean().nullable(),
  /** null for every non-quiz kind; `correct` present per the caller check above. */
  questions: z.array(trainingQuizQuestionAuthoringSchema.extend({ id: uuidSchema })).nullable(),
  // ---- Training redesign ----
  sectionId: uuidSchema.nullable(),
  description: z.string().nullable(),
  /** `video_url` pointing at YouTube/Vimeo; `null` for a plain video file and every other kind. */
  embedProvider: trainingEmbedProviderSchema.nullable(),
  embedId: z.string().nullable(),
  // ---- Several languages (additive; absent = English only) ----
  language: trainingLanguageSchema.optional(),
  translations: trainingTextTranslationsSchema.optional(),
  /** Video lessons: the extra language versions, each with a playback `url` for the builder preview. */
  variants: z.array(trainingVideoVariantAuthoringSchema).optional(),
});
export type TrainingContentItemAuthoring = z.infer<typeof trainingContentItemAuthoringSchema>;

export const trainingTaskDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  lineageId: uuidSchema,
  version: z.number().int(),
  category: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  legalEntityId: uuidSchema.nullable(),
  allottedMinutes: z.number().int(),
  requiresWebcam: z.boolean(),
  blocksClockOut: z.boolean(),
  sequentialModules: z.boolean(),
  certificateEnabled: z.boolean(),
  status: trainingTaskStatusSchema,
  items: z.array(trainingContentItemAuthoringSchema),
  assignmentCount: z.number().int(),
  createdByPersonId: uuidSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
  // ---- Training redesign ----
  categoryId: uuidSchema.nullable(),
  sections: z.array(trainingSectionSchema),
  /** Private bucket key of the uploaded cover (send it back unchanged on save) + a short-lived GET. */
  coverImageKey: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  /** Strict playback: 1× speed and no seeking past what was watched. Strict MODE as a whole is
   * `requiresWebcam || blocksClockOut || strictPlayback` — see `isStrictTrainingCourse()`. */
  strictPlayback: z.boolean(),
  certificateValidityMonths: z.number().int().nullable(),
  /** The certificate design (PLAN §13); null → the company's default design. */
  certificateTemplateId: uuidSchema.nullable(),
  competencies: z.array(trainingCourseCompetencySchema),
  lessonCount: z.number().int(),
  totalDurationSeconds: z.number().int(),
  /** The course's short link code (`/learning/c/<code>/<name>`), the same for every version. */
  shareCode: z.string(),
  // ---- Several languages (additive; absent = English only) ----
  language: trainingLanguageSchema.optional(),
  translations: trainingTextTranslationsSchema.optional(),
  /** The languages the course is offered in, its own included (see `trainingCourseLanguagesSchema`). */
  languages: trainingCourseLanguagesSchema.optional(),
});
export type TrainingTaskDto = z.infer<typeof trainingTaskDtoSchema>;

/** A course share code as it appears in a link: the API's alphabet (no 0/1/i/l/o), 6–12 characters. */
export const trainingCourseShareCodeSchema = z.string().regex(/^[a-z0-9]{6,12}$/);

/** A row of the HR course library. A superset of the summary, so the existing library screen keeps
 * working; the redesign's card grid reads the extra counts. `assignedCount` / `completedCount`
 * span every version of the course (its lineage), cancelled assignments excluded. */
export const trainingCourseListItemSchema = trainingTaskSummarySchema.extend({
  lineageId: uuidSchema,
  version: z.number().int(),
  status: trainingTaskStatusSchema,
  categoryId: uuidSchema.nullable(),
  strictPlayback: z.boolean(),
  assignedCount: z.number().int(),
  completedCount: z.number().int(),
  updatedAt: instantSchema,
  /** The short link code; null only in the instant before a brand-new course gets one. */
  shareCode: z.string().nullable(),
});
export type TrainingCourseListItem = z.infer<typeof trainingCourseListItemSchema>;

export const listTrainingTasksQuerySchema = paginationQuerySchema.extend({
  status: trainingTaskStatusSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
  // ---- Training redesign ----
  categoryId: uuidSchema.optional(),
  /** Only courses in strict mode (webcam, clock-out lock or strict playback) — the review notice's filter. */
  strict: queryBooleanSchema.optional(),
});

export const createTrainingTaskBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  category: z.string().trim().min(1).max(100),
  legalEntityId: uuidSchema.optional(),
  /** Optional since the redesign: omitted → the server derives it from the lessons (video length,
   * document pages, article words), so HR is never asked for a number they cannot know. */
  allottedMinutes: z.number().int().positive().optional(),
  // Strict mode is OFF by default for new courses (redesign decision 8.1). Existing courses keep
  // what is stored — a PATCH that omits these never touches them (zod 3 `.partial()` drops defaults).
  requiresWebcam: z.boolean().default(false),
  blocksClockOut: z.boolean().default(false),
  sequentialModules: z.boolean().default(false),
  certificateEnabled: z.boolean().default(false),
  items: z.array(trainingContentItemInputSchema).min(1),
  // ---- Training redesign ----
  categoryId: uuidSchema.optional(),
  sections: z.array(trainingSectionInputSchema).max(50).optional(),
  /** From `contentUploadTicket` with `kind: 'cover'`; `null` on patch removes the cover. */
  coverImageKey: z.string().min(1).nullable().optional(),
  strictPlayback: z.boolean().default(false),
  certificateValidityMonths: z.number().int().min(1).max(120).nullable().optional(),
  competencies: z.array(trainingCourseCompetencyInputSchema).max(20).optional(),
  /** A design from `trainingCertificateTemplates`; `null` on patch → back to the company default. */
  certificateTemplateId: uuidSchema.nullable().optional(),
  /** The language `title` / `description` are written in. Omitted → English. */
  language: trainingLanguageSchema.optional(),
  /** The course's title and description in the other languages. */
  translations: trainingTextTranslationsSchema.optional(),
  /** The languages the course is offered in, its own included (see `trainingCourseLanguagesSchema`).
   * The API normalises it. */
  languages: trainingCourseLanguagesSchema.optional(),
});

/**
 * Partial edit. D1: when the task already has assignments, the handler forks a new
 * `(lineageId, version + 1)` row rather than mutating one that a learner may be mid-way through —
 * hence "maybe a new id" in the response. The client always re-reads `id` from the 200, never
 * assumes it matches the `:taskId` it PATCHed.
 */
export const patchTrainingTaskBodySchema = createTrainingTaskBodySchema.partial();

/** Course cover images (redesign §10): what every browser renders in an `<img>`. */
export const TRAINING_COVER_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const TRAINING_COVER_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** External certificates HR uploads (PLAN §13): what people actually receive from outside. */
export const TRAINING_EXTERNAL_CERTIFICATE_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const TRAINING_EXTERNAL_CERTIFICATE_MAX_BYTES = 10 * 1024 * 1024;

export const trainingContentUploadTicketBodySchema = z.object({
  /**
   * `cover` (redesign): a course cover image — JPEG/PNG/WebP up to 5 MB.
   * `certificate_asset` (PLAN §13): a design's background, logo or signature — JPEG/PNG/WebP up to 5 MB.
   * `external_certificate` (PLAN §13): an uploaded certificate file — PDF/JPEG/PNG up to 10 MB.
   */
  kind: z.enum(['document', 'video', 'cover', 'certificate_asset', 'external_certificate']),
  mimeType: z.string(),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(TRAINING_VIDEO_UPLOAD_MAX_BYTES),
  fileName: z.string().trim().min(1).max(255),
});
/**
 * A document lesson from a file the caller attached in the AI copilot chat (`POST /ai/attachments`
 * — a PDF up to 10 MB), by id. The route copies the file into the course content area and appends
 * the lesson, exactly as a save from the course builder would; the bytes never travel in a body.
 */
export const addTrainingLessonFromAttachmentBodySchema = z.object({
  /** One of the CALLER's own chat attachments. */
  attachmentId: uuidSchema,
  /** Omitted → the attachment's file name without its extension. */
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  required: z.boolean().default(true),
  /** A section of this course (`trainingTasks.get` → `sections[].id`). Omitted → the course's last
   * section, or no section when the course has none. */
  sectionId: uuidSchema.optional(),
});

export const trainingContentUploadTicketResponseSchema = z.object({
  storageKey: z.string(),
  putUrl: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: instantSchema,
});

export const archiveTrainingTaskResponseSchema = trainingTaskDtoSchema;

export const publishTrainingTaskResponseSchema = trainingTaskDtoSchema;

// `previewTrainingTaskResponseSchema` is declared further below, once `trainingContentItemDetailSchema`
// (the learner-facing item shape it reuses) exists — see the Task Detail section.

// ---- Category CRUD (plan §3.7, MVP) ---------------------------------------------------------------

export const trainingCategorySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  sortOrder: z.number().int(),
  taskCount: z.number().int(),
  createdAt: instantSchema,
});
export type TrainingCategory = z.infer<typeof trainingCategorySchema>;

export const createTrainingCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  sortOrder: z.number().int().optional(),
});
export const patchTrainingCategoryBodySchema = createTrainingCategoryBodySchema.partial();

// ---- Audience / due date shapes, shared by assign + batches (plan §3.7) --------------------------

export const trainingAudienceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('people'), personIds: z.array(uuidSchema).min(1).max(200) }),
  z.object({ kind: z.literal('all_active'), ids: z.array(uuidSchema).optional() }),
  z.object({ kind: z.literal('legal_entity'), ids: z.array(uuidSchema).optional() }),
  z.object({ kind: z.literal('location'), ids: z.array(uuidSchema).optional() }),
  z.object({ kind: z.literal('department'), ids: z.array(uuidSchema).optional() }),
  z.object({
    kind: z.literal('probation_cohort'),
    joinedWithinDays: z.number().int().min(1).max(180),
  }),
]);
export type TrainingAudience = z.infer<typeof trainingAudienceSchema>;

export const trainingAssignmentDueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), dueDate: dateOnlySchema, dueTime: trainingTimeOfDaySchema }),
  z.object({
    kind: z.literal('relative_to_join'),
    days: z.number().int().min(1).max(90),
    dueTime: trainingTimeOfDaySchema,
  }),
  /**
   * No due date: the course stays available for the person's lifetime. The assignment's `dueAt` is
   * null, so it is never `overdue`/`due_soon`, never blocks clock-out (even when mandatory), gets
   * no due/overdue reminders or escalations, and never counts as overdue. `isMandatory` is an
   * independent toggle.
   */
  z.object({ kind: z.literal('none') }),
]);
export type TrainingAssignmentDue = z.infer<typeof trainingAssignmentDueSchema>;

export const trainingAssignmentSkipReasonSchema = z.enum([
  'out_of_scope',
  'already_assigned',
  'inactive',
  'non_working_due_day',
]);

/**
 * `POST /training/assignments` body (plan §3.2 extended by §3.7's audience/due union — the MVP's
 * flat `{personIds, dueDate, dueTime}` shape never shipped as a route, so this IS the route,
 * not a later replacement of one).
 */
export const assignTrainingTaskBodySchema = z.object({
  taskId: uuidSchema,
  audience: trainingAudienceSchema,
  due: trainingAssignmentDueSchema,
  autoEnrolNewJoiners: z.boolean().optional(),
  isMandatory: z.boolean().optional(),
  allottedMinutes: z.number().int().positive().optional(),
  assignedOn: dateOnlySchema.optional(),
});
export type AssignTrainingTaskBody = z.infer<typeof assignTrainingTaskBodySchema>;

/** Synchronous path: the audience resolved to ≤ 200 people and every row was created inline. */
export const assignTrainingTaskResponseSchema = z.object({
  batchId: uuidSchema,
  createdCount: z.number().int(),
  skipped: z.array(z.object({ personId: uuidSchema, reason: trainingAssignmentSkipReasonSchema })),
});
export type AssignTrainingTaskResponse = z.infer<typeof assignTrainingTaskResponseSchema>;

/** Async path (plan §3.7): the audience exceeded 200 people, so `training.batch.expand` is doing
 * the work in the worker. The client polls `GET /training/batches/:batchId` for the outcome. */
export const assignTrainingTaskAcceptedResponseSchema = z.object({
  batchId: uuidSchema,
  status: z.literal('expanding'),
});
export type AssignTrainingTaskAcceptedResponse = z.infer<
  typeof assignTrainingTaskAcceptedResponseSchema
>;

export const trainingBatchStatusSchema = z.enum(['open', 'closed']);

export const trainingBatchSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  taskId: uuidSchema,
  taskTitle: z.string(),
  audience: trainingAudienceSchema,
  due: trainingAssignmentDueSchema,
  autoEnrolNewJoiners: z.boolean(),
  isMandatory: z.boolean(),
  createdCount: z.number().int(),
  skippedCount: z.number().int(),
  status: trainingBatchStatusSchema,
  closedAt: instantSchema.nullable(),
  createdByPersonId: uuidSchema,
  createdAt: instantSchema,
});
export type TrainingBatch = z.infer<typeof trainingBatchSchema>;

export const listTrainingBatchesQuerySchema = paginationQuerySchema.extend({
  taskId: uuidSchema.optional(),
  status: trainingBatchStatusSchema.optional(),
});

export const trainingAudiencePreviewQuerySchema = z.object({
  taskId: uuidSchema,
  kind: z.enum([
    'people',
    'all_active',
    'legal_entity',
    'location',
    'department',
    'probation_cohort',
  ]),
  personIds: z.array(uuidSchema).optional(),
  ids: z.array(uuidSchema).optional(),
  joinedWithinDays: z.coerce.number().int().min(1).max(180).optional(),
});
export const trainingAudiencePreviewResponseSchema = z.object({
  count: z.number().int(),
  sampleNames: z.array(z.string()).max(10),
  byLocation: z.array(
    z.object({ locationId: uuidSchema, locationName: z.string(), count: z.number().int() }),
  ),
});

export const trainingAssignmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  task: trainingTaskSummarySchema,
  assignedByPersonId: uuidSchema,
  assignedByName: z.string(),
  assignedOn: dateOnlySchema,
  availableFrom: instantSchema,
  /** `null` = no due date (lifetime access): never overdue/due-soon, never blocks clock-out. */
  dueAt: instantSchema.nullable(),
  allottedMinutes: z.number().int(),
  status: trainingAssignmentStatusSchema,
  /** Computed by trainingDisplayStatus() at read time — see D2 in the plan. Never stored. */
  displayStatus: trainingDisplayStatusSchema,
  /** Decimal(5,2) as a string, never a JS float — CLAUDE.md invariant 2's spirit for any precise
   * quantity, not only money. */
  progressPercent: z.string(),
  activeSeconds: z.number().int(),
  startedAt: instantSchema.nullable(),
  completedAt: instantSchema.nullable(),
  overrunMinutes: z.number().int().nullable(),
  incompleteReason: z.string().nullable(),
  createdAt: instantSchema,
  // ---- Training redesign (My learning cards, Learners tab) ----
  /** From the assigning batch; assignments made before batches existed count as required. */
  isMandatory: z.boolean(),
  /** Last heartbeat that credited anything — "Continue learning" picks the most recent. */
  lastActivityAt: instantSchema.nullable(),
  /** Lessons of the assigned version / those this person has finished — "3 of 8 lessons". */
  lessonsTotal: z.number().int(),
  lessonsCompleted: z.number().int(),
});
export type TrainingAssignment = z.infer<typeof trainingAssignmentSchema>;

export const trainingTodayAggregateSchema = z.object({
  total: z.number().int(),
  completed: z.number().int(),
  inProgress: z.number().int(),
  overdue: z.number().int(),
  allottedMinutes: z.number().int(),
  activeMinutes: z.number().int(),
  percent: z.number().min(0).max(100),
});
export type TrainingTodayAggregate = z.infer<typeof trainingTodayAggregateSchema>;

export const trainingTodaySchema = z.object({
  /** The caller's local calendar date, resolved in their current location's timezone — never the
   * server's or the browser's (CLAUDE.md invariant 3). */
  date: dateOnlySchema,
  timezone: timezoneSchema,
  aggregate: trainingTodayAggregateSchema,
  /** Due today or earlier, in progress, or completed today — the same "today" shape as the plan's
   * `GET /training/me/today` (§3.4). Not the caller's full history. */
  assignments: z.array(trainingAssignmentSchema),
  /**
   * Read-only preview of the clock-out gate (Training_Module_Plan.md §4.3): true when at least one
   * of `assignments` blocks clock-out today. This slice does NOT enforce it — `attendance.ts`'s
   * punch route is unchanged — so it is display-only until the gate's write side (plan TG-7) lands.
   */
  clockOutBlocked: z.boolean(),
  blockingCount: z.number().int(),
  /** The first active, overdue, clock-out-blocking assignment, if any — drives the sticky overrun
   * banner (plan §6.1). `null` once nothing is both overdue and still blocking. */
  overrun: z
    .object({ assignmentId: uuidSchema, title: z.string(), dueAt: instantSchema.nullable() })
    .nullable(),
});
export type TrainingToday = z.infer<typeof trainingTodaySchema>;

/**
 * Plan §3.2: `list`'s scope grew from "always the caller's own" to `own | team | org`, resolved in
 * the handler (`routeMeta(null)` + `can()`) rather than the permission axis — a manager needs
 * `team` without holding `learning:manage`. `display` filters on the DERIVED status (D2), never a
 * stored column.
 */
export const listTrainingAssignmentsQuerySchema = paginationQuerySchema.extend({
  scope: z.enum(['own', 'team', 'org']).default('own'),
  status: trainingAssignmentStatusSchema.optional(),
  display: z.enum(['overdue', 'due_soon']).optional(),
  personId: uuidSchema.optional(),
  /**
   * Only assignments of this course — matched by LINEAGE, so any version of it (D1: editing an
   * assigned course forks a new task id). Backs the shareable `/training/courses/:taskId` link,
   * which resolves a course to the caller's own assignment of it.
   */
  taskId: uuidSchema.optional(),
  /** Same as `taskId`, by the course's short link code (`/learning/c/<code>`). Unknown → no rows. */
  courseCode: trainingCourseShareCodeSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

// ---- Consent (webcam recording notice, §5.5) ----------------------------------------------------

export const trainingConsentStatusSchema = z.object({
  required: z.boolean(),
  noticeVersion: z.string(),
  noticeText: z.string(),
  given: z.boolean(),
  givenAt: instantSchema.nullable(),
});

export const giveTrainingConsentBodySchema = z.object({ noticeVersion: z.string() });

export const withdrawTrainingConsentResponseSchema = z.object({ withdrawn: z.literal(true) });

// ---- Certificates and competencies (plan §3.7, Phase 2A, D17/D18) ---------------------------------

export const trainingCertificateStatusSchema = z.enum(['generating', 'issued', 'revoked']);

export const trainingCertificateSchema = z.object({
  id: uuidSchema,
  certificateNo: z.string(),
  taskId: uuidSchema,
  taskTitle: z.string(),
  issuedAt: instantSchema,
  expiresAt: instantSchema.nullable(),
  status: trainingCertificateStatusSchema,
  verificationCode: z.string(),
  // ---- Certificates (PLAN §13) ----
  /** The name printed, snapshotted at issue; null on certificates issued before it was stored. */
  holderName: z.string().nullable(),
  /** 1 at issue, +1 per reissue. */
  revision: z.number().int(),
});
export type TrainingCertificate = z.infer<typeof trainingCertificateSchema>;

export const trainingCompetencyAwardSchema = z.object({
  id: uuidSchema,
  competencyId: uuidSchema,
  competencyName: z.string(),
  level: z.string(),
  source: z.literal('training'),
  sourceId: uuidSchema,
  awardedAt: instantSchema,
  expiresAt: instantSchema.nullable(),
});
export type TrainingCompetencyAward = z.infer<typeof trainingCompetencyAwardSchema>;

export const trainingMeContract = c.router(
  {
    today: {
      method: 'GET',
      path: '/today',
      responses: {
        200: trainingTodaySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "The caller's own training to-do list (courses and lessons due), today's completion, "
        + 'and a read-only clock-out gate preview',
      metadata: routeMeta('learning:read'),
    },
    getConsent: {
      method: 'GET',
      path: '/consent',
      responses: { 200: trainingConsentStatusSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Whether the caller has acknowledged the webcam recording notice that strict courses '
        + 'need (§5.5)',
      metadata: routeMeta('learning:read'),
    },
    giveConsent: {
      method: 'POST',
      path: '/consent',
      headers: idempotencyHeadersSchema,
      body: giveTrainingConsentBodySchema,
      responses: { 201: trainingConsentStatusSchema, 401: errorBodySchema, 403: errorBodySchema, 409: errorBodySchema },
      summary: 'Record acknowledgement of the webcam recording notice, versioned',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    withdrawConsent: {
      method: 'POST',
      path: '/consent/withdraw',
      body: z.object({}),
      responses: { 200: withdrawTrainingConsentResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Withdraw webcam recording consent. HR is notified (§5.5)',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    listCertificates: {
      method: 'GET',
      path: '/certificates',
      responses: { 200: z.array(trainingCertificateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: "The caller's own course certificates (number, course, issued, expires, status)",
      metadata: routeMeta('learning:read'),
    },
    listCompetencies: {
      method: 'GET',
      path: '/competencies',
      responses: { 200: z.array(trainingCompetencyAwardSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary:
        "The caller's own skills (competencies) awarded by completing courses, with the course "
        + 'that awarded each',
      metadata: routeMeta('learning:read'),
    },
  },
  { pathPrefix: '/training/me' },
);

/**
 * Task Detail (plan §6.1, frames 02-03): the content player and the proctored knowledge check.
 * `get`/`start`/`complete`/`report-issue` are self-scoped reads/writes on the caller's own
 * assignment — same reasoning as `trainingMe.today` above (routeMeta('learning:read') plus an
 * in-handler `can(actor,'learning:read', selfTarget(actor))`; a manager/HR scope on `get` is a
 * later addition, per the plan's §3.2 table). `sessions.*` and quiz attempts are proctored, so they
 * never surface as an AI copilot tool (D13): a model must not "complete my training" or "answer my
 * quiz" on the caller's behalf.
 */

export const trainingQuizQuestionSchema = z.object({
  id: uuidSchema,
  type: trainingQuizQuestionTypeSchema,
  prompt: z.string(),
  options: z.array(z.object({ key: z.string(), label: z.string() })),
  // `correct` is intentionally absent — never sent to a learner (D15). The authoring shape that
  // includes it lives on `trainingTasks`, a later contract addition for `learning:manage` callers.
});
export type TrainingQuizQuestion = z.infer<typeof trainingQuizQuestionSchema>;

/** A content item as the assignee sees it: locked state and per-item progress folded in, so the
 * module list and the player never re-derive the sequential-unlock rule (§4.2) client-side. */
export const trainingContentItemDetailSchema = z.object({
  id: uuidSchema,
  sortOrder: z.number().int(),
  kind: trainingContentKindSchema,
  title: z.string(),
  required: z.boolean(),
  /** True when an earlier required item is not yet complete (§4.2's sequential-unlock rule). */
  locked: z.boolean(),
  percent: z.number().min(0).max(100),
  completedAt: instantSchema.nullable(),
  durationSeconds: z.number().int().nullable(),
  /** `video_url`: the author's link. `video_upload`/`document`: a short-lived presigned GET for the
   * private object — the storage key itself never reaches a learner. */
  url: z.string().nullable(),
  mimeType: z.string().nullable(),
  pageCount: z.number().int().nullable(),
  bodyMarkdown: z.string().nullable(),
  wordCount: z.number().int().nullable(),
  checklist: z.array(z.object({ key: z.string(), label: z.string() })).nullable(),
  /** checklist only — which keys this assignee has already ticked. */
  checkedKeys: z.array(z.string()),
  /** quiz only (kind = 'quiz'); null for every other kind. */
  passMarkPercent: z.number().int().nullable(),
  maxAttempts: z.number().int().nullable(),
  timeLimitMinutes: z.number().int().nullable(),
  attemptsUsed: z.number().int().nullable(),
  attemptsLeft: z.number().int().nullable(),
  bestScorePercent: z.string().nullable(),
  passed: z.boolean().nullable(),
  // ---- Training redesign (course player) ----
  sectionId: uuidSchema.nullable(),
  description: z.string().nullable(),
  embedProvider: trainingEmbedProviderSchema.nullable(),
  embedId: z.string().nullable(),
  /** Where playback stopped last time (seconds) — the player seeks here on open. Video only. */
  lastPositionSeconds: z.number().int().nullable(),
  // ---- Several languages (additive; absent = English only) ----
  /** The language `title`, `description` and the lesson's own video are in. */
  language: trainingLanguageSchema.optional(),
  /** Title and description in the other languages — the player picks one client-side, so switching
   * language needs no refetch. Quiz questions are the exception: see `startQuizAttemptBodySchema`. */
  translations: trainingTextTranslationsSchema.optional(),
  /** Video lessons: the other language versions, each with its own playback `url`. */
  variants: z.array(trainingVideoVariantDetailSchema).optional(),
  /** Where playback stopped last time in each OTHER language version (seconds), so switching resumes
   * that version at its own second. The lesson's own language stays in `lastPositionSeconds`. A
   * missing key = never played in that language. */
  lastPositionByLanguage: z.record(trainingLanguageSchema, z.number().int().min(0)).optional(),
  /** Coverage of each language version watched so far, the lesson's own language included. `percent`
   * is the best of these, so the strict player caps seeking per version from this map. Omitted → the
   * lesson was only ever watched in its own language and `percent` is that version's. */
  percentByLanguage: z.record(trainingLanguageSchema, z.number().min(0).max(100)).optional(),
});
export type TrainingContentItemDetail = z.infer<typeof trainingContentItemDetailSchema>;

export const trainingAssignmentDetailSchema = trainingAssignmentSchema.extend({
  items: z.array(trainingContentItemDetailSchema),
  /** The caller's currently open session on this assignment, if any — lets the client resume a
   * session across a page reload instead of always opening a new one. */
  activeSessionId: uuidSchema.nullable(),
  consentRequired: z.boolean(),
  consentGiven: z.boolean(),
  /**
   * The webcam rule for THIS learner on THIS assignment, resolved by the server from the course,
   * the company's webcam mode and any waiver HR granted: `required` (no session without a live
   * camera), `optional` (record when a camera is available, carry on without one), `off` (never
   * ask). The player follows this, never the course's `requiresWebcam` flag on its own.
   */
  webcamMode: z.enum(['required', 'optional', 'off']),
  // ---- Training redesign (course player) ----
  /** Sections of the assigned version, in order. Empty → render the lessons as one list. */
  sections: z.array(trainingSectionSchema),
  /** The lesson to open — the last one touched, else the first unfinished, else the first. */
  lastItemId: uuidSchema.nullable(),
  strictPlayback: z.boolean(),
  /** Issued certificate, shown on the completion screen and the Certificate tab. */
  certificate: z
    .object({ id: uuidSchema, certificateNo: z.string(), issuedAt: instantSchema })
    .nullable(),
});
export type TrainingAssignmentDetail = z.infer<typeof trainingAssignmentDetailSchema>;

/** Author-as-learner preview (plan §3.7): the exact learner-facing payload, quiz questions
 * WITHOUT `correct`. Creates no assignment/session/recording/progress. */
export const previewTrainingTaskResponseSchema = z.object({
  task: trainingTaskSummarySchema,
  items: z.array(trainingContentItemDetailSchema),
  /** Redesign: so the preview renders the real player's section outline. */
  sections: z.array(trainingSectionSchema),
});

export const trainingCompleteBodySchema = z.object({ acknowledged: z.literal(true) });

export const trainingReportIssueBodySchema = z.object({
  category: trainingIssueCategorySchema,
  note: z.string().trim().max(500).optional(),
});
export const trainingIssueAcceptedSchema = z.object({ accepted: z.literal(true) });

export const startQuizAttemptBodySchema = z.object({
  sessionId: uuidSchema,
  /** Serve the questions in this language; a question without it comes back in its own language.
   * Omitted → each question in its own language. Never affects scoring — answers are option keys. */
  language: trainingLanguageSchema.optional(),
});
export const startQuizAttemptResponseSchema = z.object({
  attemptId: uuidSchema,
  attemptNo: z.number().int(),
  attemptsLeft: z.number().int(),
  deadlineAt: instantSchema.nullable(),
  passMarkPercent: z.number().int(),
  questions: z.array(trainingQuizQuestionSchema),
});
export type StartQuizAttemptResponse = z.infer<typeof startQuizAttemptResponseSchema>;

// ---- Assignment management mutations (plan §3.2: PATCH/cancel/reopen/mark-incomplete/waive) ------

/**
 * `clearDue: true` removes the due date (the assignment becomes "no due date" / lifetime access).
 * It cannot be combined with `dueDate`/`dueTime` (400). Setting `dueDate` on an assignment that
 * has no due date requires `dueTime` too, since there is no existing time to keep.
 */
export const patchTrainingAssignmentBodySchema = z.object({
  dueDate: dateOnlySchema.optional(),
  dueTime: trainingTimeOfDaySchema.optional(),
  clearDue: z.literal(true).optional(),
  allottedMinutes: z.number().int().positive().optional(),
});

export const cancelTrainingAssignmentBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/**
 * Either a new due date (`dueDate` + `dueTime`, both required together) or `clearDue: true` to
 * reopen with no due date. Exactly one of the two must be sent (400 otherwise).
 */
export const reopenTrainingAssignmentBodySchema = z.object({
  dueDate: dateOnlySchema.optional(),
  dueTime: trainingTimeOfDaySchema.optional(),
  clearDue: z.literal(true).optional(),
});

export const markTrainingAssignmentIncompleteBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/** Self-excluded via `learning.override:execute` — the handler must reject a caller waiving their
 * own webcam requirement, same shape as the release-clock-out self-exclusion in `trainingAdmin`. */
export const waiveTrainingWebcamBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

// ---- Recordings (plan §3.2, `learning.recording:view`, always `ai: 'hidden'`) ---------------------

export const trainingRecordingSegmentSummarySchema = z.object({
  seq: z.number().int(),
  startedAt: instantSchema,
  durationMs: z.number().int(),
  uploadedAt: instantSchema.nullable(),
});

export const trainingRecordingSummarySchema = z.object({
  sessionId: uuidSchema,
  recordingId: uuidSchema,
  startedAt: instantSchema,
  endedAt: instantSchema.nullable(),
  segments: z.array(trainingRecordingSegmentSummarySchema),
  /** Total uncovered time across the segment timeline — the gap detector behind `verificationStatus`. */
  gapSeconds: z.number(),
  verificationStatus: trainingVerificationStatusSchema,
});
export type TrainingRecordingSummary = z.infer<typeof trainingRecordingSummarySchema>;

export const playbackTrainingRecordingBodySchema = z.object({
  purpose: z.string().trim().min(1).max(200),
});
export const playbackTrainingRecordingResponseSchema = z.object({
  segments: z.array(
    z.object({
      seq: z.number().int(),
      url: z.string(),
      startedAt: instantSchema,
      durationMs: z.number().int(),
    }),
  ),
  expiresAt: instantSchema,
});

// ---- Overrun regularisation (plan §4.4) -----------------------------------------------------------

export const submitTrainingOverrunRegularisationBodySchema = z.object({
  // Reuses attendance.contract.ts's enum rather than redeclaring it — that file already defines it
  // for `attendanceRegularisationDtoSchema.reasonCategory`, and this submit body's value becomes
  // that exact field, so one literal set backs both instead of two that could drift apart.
  reasonCategory: trainingOverrunRegularisationReasonCategorySchema,
  explanation: z.string().trim().min(20).max(1000),
  evidenceNotes: z.string().trim().max(1000).optional(),
  attachmentIds: z.array(uuidSchema).max(5).optional(),
});

export const trainingAssignmentsContract = c.router(
  {
    // Declared before `:assignmentId` routes so a literal path segment is never swallowed by the
    // param route (same reasoning as `recordings/:recordingId/playback` below, which is genuinely
    // a sibling resource rather than a sub-path of one assignment).
    audiencePreview: {
      method: 'GET',
      path: '/audience-preview',
      query: trainingAudiencePreviewQuerySchema,
      responses: {
        200: trainingAudiencePreviewResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Preview who a course assignment would reach before assigning: count and sample of the '
        + 'people an audience (people, departments, locations, legal entities, all employees, '
        + 'recent joiners) resolves to, e.g. "48 people across 3 locations"',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    assign: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: assignTrainingTaskBodySchema,
      responses: {
        201: assignTrainingTaskResponseSchema,
        202: assignTrainingTaskAcceptedResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Assign (enrol) a published course to learners: specific people (Person ids), '
        + 'departments, locations, legal entities, all active employees, or recent joiners '
        + '(probation cohort); with a due date (fixed date + time, N days after joining, or '
        + 'none), mandatory flag and optional auto-enrol of new joiners. People beyond the '
        + "caller's scope or already assigned are skipped. Over 200 people runs in the background "
        + '(202 + batchId)',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingAssignmentsQuerySchema,
      responses: {
        200: paginatedSchema(trainingAssignmentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "Course assignments: the caller's own, their team's or the org's (scope), filterable by "
        + 'person, course (taskId, any version), status, overdue / due soon and due-date range — '
        + 'My learning deadlines and the admin monitor',
      // Signed-in only, like `get`: the handler requires `learning:read` for the caller's own list
      // and HR / manager scope for another person's — neither is unconditional.
      metadata: routeMeta(null),
    },
    get: {
      method: 'GET',
      path: '/:assignmentId',
      responses: {
        200: trainingAssignmentDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'One course assignment: its lessons with locked/progress state and consent status. '
        + 'Self, manager or HR scope is checked in the handler; 404 when not in scope',
      metadata: routeMeta(null),
    },
    start: {
      method: 'POST',
      path: '/:assignmentId/start',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: trainingAssignmentDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'First open of Task Detail — not_started -> started (T1). 412 CONSENT_REQUIRED / '
        + 'NOT_CLOCKED_IN / NOT_YET_AVAILABLE',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    complete: {
      method: 'POST',
      path: '/:assignmentId/complete',
      headers: idempotencyHeadersSchema,
      body: trainingCompleteBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'in_progress -> completed (T3) once every required item is at 100%. 412 REQUIREMENTS_NOT_MET',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    reportIssue: {
      method: 'POST',
      path: '/:assignmentId/report-issue',
      headers: idempotencyHeadersSchema,
      body: trainingReportIssueBodySchema,
      responses: {
        202: trainingIssueAcceptedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Report a camera or content problem on the caller's course assignment: notifies HR and "
        + 'is evidence for a later overrun regularisation',
      metadata: routeMeta('learning:read'),
    },
    overrunRegularisation: {
      method: 'POST',
      path: '/:assignmentId/overrun-regularisation',
      headers: idempotencyHeadersSchema,
      body: submitTrainingOverrunRegularisationBodySchema,
      responses: {
        201: attendanceRegularisationDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Ask to regularise time spent past the allotted time on a course finished late (kind '
        + 'training_overrun, §4.4). 409 already submitted; 412 not completed late',
      metadata: routeMeta('attendance.regularisation:request'),
    },
    patch: {
      method: 'PATCH',
      path: '/:assignmentId',
      headers: idempotencyHeadersSchema,
      body: patchTrainingAssignmentBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Change one learner's course due date (dueDate + dueTime) — e.g. extend a deadline — "
        + 'clear it (clearDue: no due date), or change the allotted time. Logged as due_changed',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    cancel: {
      method: 'POST',
      path: '/:assignmentId/cancel',
      body: cancelTrainingAssignmentBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Unassign a course from one person: cancels their open assignment, with a reason (T6). '
        + '409 if already completed',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    reopen: {
      method: 'POST',
      path: '/:assignmentId/reopen',
      body: reopenTrainingAssignmentBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Reopen a course assignment that was closed as incomplete, with a new due date or none '
        + '(clearDue). Progress is kept and quiz attempts reset (T5)',
      metadata: routeMeta('learning:manage'),
    },
    markIncomplete: {
      method: 'POST',
      path: '/:assignmentId/mark-incomplete',
      body: markTrainingAssignmentIncompleteBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "HR closes a learner's course assignment as incomplete, with a reason (T4d)",
      metadata: routeMeta('learning:manage'),
    },
    waiveWebcam: {
      method: 'POST',
      path: '/:assignmentId/waive-webcam',
      body: waiveTrainingWebcamBodySchema,
      responses: {
        200: trainingAssignmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Waive the webcam requirement on one person's course assignment, with a reason. A "
        + 'caller may not waive their own',
      metadata: routeMeta('learning.override:execute'),
    },
    listRecordings: {
      method: 'GET',
      path: '/:assignmentId/recordings',
      responses: {
        200: z.array(trainingRecordingSummarySchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Sessions plus segment timeline and gaps, behind the verification status',
      metadata: routeMeta('learning.recording:view', { ai: 'hidden' }),
    },
    playbackRecording: {
      method: 'POST',
      path: '/recordings/:recordingId/playback',
      body: playbackTrainingRecordingBodySchema,
      responses: {
        200: playbackTrainingRecordingResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Writes a TrainingRecordingAccess row and returns 5-minute presigned GETs for every segment',
      metadata: routeMeta('learning.recording:view', { ai: 'hidden' }),
    },
    startQuizAttempt: {
      method: 'POST',
      path: '/:assignmentId/quiz/:itemId/attempts',
      body: startQuizAttemptBodySchema,
      responses: {
        201: startQuizAttemptResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Start a proctored quiz attempt inside an open session. 412 ATTEMPTS_EXHAUSTED / '
        + 'SESSION_REQUIRED / CAMERA_REQUIRED / MODULE_LOCKED',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/assignments' },
);

/**
 * `startQuizAttempt` is declared on `trainingAssignmentsContract` above for its path shape
 * (`/training/assignments/:assignmentId/quiz/:itemId/attempts`), but its proctoring logic is
 * implemented alongside the rest of the quiz/session pipeline
 * (`apps/api/src/routes/training-sessions.ts`), not with the other `trainingAssignments` routes
 * (`apps/api/src/routes/training.ts`). That file mounts this single route as its own tiny Express
 * router rather than the whole `trainingAssignmentsContract` (which would re-register — and,
 * depending on mount order, could shadow or be shadowed by — the other agent's routes for the same
 * contract).
 *
 * This alias exists ONLY so `apps/api/src/lib/route-permissions.ts`'s scanner can resolve the route:
 * it looks up a mounted router by the literal source text of the first argument passed to
 * `tsRestRouter(...)`, then reads `@hrms/shared`'s export of that exact name — an ad-hoc object
 * built inline in a route file (not a real export) can never resolve that way. No new route, schema,
 * or behaviour is introduced here — it is the same `AppRoute` object, under a second name.
 */
export const trainingQuizAttemptStartContract = {
  startQuizAttempt: trainingAssignmentsContract.startQuizAttempt,
};

/**
 * The other half of that same split: every `trainingAssignmentsContract` route EXCEPT
 * `startQuizAttempt` (mounted above, as its own router, by `training-sessions.ts`). Excluded via
 * destructuring, not merely left out of the implementation object, so `apps/api/src/routes/
 * training.ts`'s `tsRestRouter` call never registers that path at all — mounting it twice (once
 * there with no real handler, once in the sibling's router) would make correctness depend on
 * `app.use()` mount order rather than on there being exactly one route for that path.
 *
 * Exported here, not computed inline in the route file, for the same reason as
 * `trainingQuizAttemptStartContract` just above: the route-permissions scanner resolves a mounted
 * router by looking up `@hrms/shared`'s export of the literal identifier passed to
 * `tsRestRouter(...)` — an object destructured inline in a route file is not a real export and can
 * never resolve that way.
 */
export const { startQuizAttempt: _startQuizAttempt, ...trainingAssignmentsOwnContract } =
  trainingAssignmentsContract;

// ---- Sessions: the heartbeat pipeline behind every content type, including the quiz (D3/D4/D14) --

export const trainingRecorderSettingsSchema = z.object({
  mimeTypes: z.array(z.string()),
  width: z.number().int(),
  height: z.number().int(),
  frameRate: z.number().int(),
  videoBitsPerSecond: z.number().int(),
});

export const openTrainingSessionBodySchema = z.object({
  assignmentId: uuidSchema,
  cameraState: trainingCameraStateSchema,
});
export const openTrainingSessionResponseSchema = z.object({
  sessionId: uuidSchema,
  recordingId: uuidSchema.nullable(),
  heartbeatIntervalSec: z.number().int(),
  segmentSeconds: z.number().int(),
  recorder: trainingRecorderSettingsSchema,
});
export type OpenTrainingSessionResponse = z.infer<typeof openTrainingSessionResponseSchema>;

export const trainingHeartbeatEventSchema = z.object({
  contentItemId: uuidSchema,
  type: z.enum(['video_range', 'page_view', 'scroll', 'check']),
  from: z.number().min(0).optional(),
  to: z.number().min(0).optional(),
  page: z.number().int().min(1).optional(),
  depth: z.number().min(0).max(1).optional(),
  key: z.string().optional(),
  /** Video: which language version was watched. Coverage is tracked per language because two
   * versions of one lesson rarely have the same length. Omitted → the lesson's own video. */
  language: trainingLanguageSchema.optional(),
});

export const trainingHeartbeatBodySchema = z.object({
  seq: z.number().int().min(1),
  sentAt: instantSchema,
  visible: z.boolean(),
  cameraLive: z.boolean(),
  events: z.array(trainingHeartbeatEventSchema).max(20),
  /** Redesign (resume): the lesson on screen and, for a video, the playhead. Stored as
   * `lastItemId` / `lastPositionSeconds`; never credits progress — `events` alone do that. */
  position: z
    .object({
      contentItemId: uuidSchema,
      seconds: z.number().min(0).max(86_400).optional(),
      /** The language version the playhead belongs to (see `trainingHeartbeatEventSchema`). */
      language: trainingLanguageSchema.optional(),
    })
    .optional(),
});

export const trainingHeartbeatResponseSchema = z.object({
  assignment: z.object({
    status: trainingAssignmentStatusSchema,
    progressPercent: z.string(),
    activeSeconds: z.number().int(),
  }),
  items: z.array(
    z.object({
      id: uuidSchema,
      percent: z.number(),
      locked: z.boolean(),
      completedAt: instantSchema.nullable(),
      /** Several languages (additive): each version's own coverage, as on the item detail, so the
       * strict player's per-version seek cap moves with the beats. Omitted for a lesson with no
       * other-language video — its `percent` already is its only version's. */
      percentByLanguage: z.record(trainingLanguageSchema, z.number().min(0).max(100)).optional(),
    }),
  ),
  pause: trainingPauseReasonSchema.nullable(),
});
export type TrainingHeartbeatResponse = z.infer<typeof trainingHeartbeatResponseSchema>;

export const presignTrainingSegmentBodySchema = z.object({
  seq: z.number().int().min(0),
  startedAt: instantSchema,
  durationMs: z.number().int().positive(),
  bytes: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024),
  mimeType: z.string(),
});
export const presignTrainingSegmentResponseSchema = z.object({
  putUrl: z.string(),
  storageKey: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: instantSchema,
});

export const endTrainingSessionBodySchema = z.object({ reason: trainingSessionEndReasonSchema });

export const trainingSessionsContract = c.router(
  {
    open: {
      method: 'POST',
      path: '/',
      body: openTrainingSessionBodySchema,
      responses: {
        201: openTrainingSessionResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Open a working session on an assignment. Ends any other open session of the caller (switched)',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    heartbeat: {
      method: 'POST',
      path: '/:sessionId/heartbeat',
      body: trainingHeartbeatBodySchema,
      responses: {
        200: trainingHeartbeatResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Liveness plus content progress for an open session — the server computes all credit (D3)',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    presignSegment: {
      method: 'POST',
      path: '/:sessionId/segments',
      body: presignTrainingSegmentBodySchema,
      responses: {
        200: presignTrainingSegmentResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Presign the PUT for one webcam recording segment (private bucket, §5.5)',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    ackSegment: {
      method: 'POST',
      path: '/:sessionId/segments/:seq/ack',
      body: z.object({}),
      responses: { 204: c.noBody(), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Confirm a segment PUT completed',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    end: {
      method: 'POST',
      path: '/:sessionId/end',
      body: endTrainingSessionBodySchema,
      responses: {
        200: z.object({ ended: z.boolean() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Pause or leave — ends the session and, when required, the recording',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/sessions' },
);

// ---- Quiz attempts (D14/D15) --------------------------------------------------------------------

export const saveQuizAnswerBodySchema = z.object({
  questionId: uuidSchema,
  answer: z.array(z.string()).max(10),
});

export const submitQuizAttemptResponseSchema = z.object({
  scorePercent: z.string(),
  passed: z.boolean(),
  passMarkPercent: z.number().int(),
  attemptsLeft: z.number().int(),
  assignment: z.object({
    status: trainingAssignmentStatusSchema,
    progressPercent: z.string(),
  }),
});
export type SubmitQuizAttemptResponse = z.infer<typeof submitQuizAttemptResponseSchema>;

export const trainingQuizAttemptsContract = c.router(
  {
    saveAnswer: {
      method: 'PUT',
      path: '/:attemptId/answers',
      body: saveQuizAnswerBodySchema,
      responses: {
        204: c.noBody(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Autosave one answer. Idempotent by nature; 412 after the deadline or submit',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
    submit: {
      method: 'POST',
      path: '/:attemptId/submit',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: submitQuizAttemptResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Score the attempt server-side (D15) and, on the first pass, mark the quiz item complete',
      metadata: routeMeta('learning:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/quiz-attempts' },
);

// ===================================================================================================
// Task library / authoring routers (plan §3.1, §3.7)
// ===================================================================================================

export const trainingTasksContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingTasksQuerySchema,
      responses: {
        200: paginatedSchema(trainingCourseListItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'List LMS courses (drafts, published = status active, archived) with version, lesson '
        + 'count and assigned/completed counts; filter by status, category or search text',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    get: {
      method: 'GET',
      path: '/:taskId',
      responses: {
        200: trainingTaskDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'One course in full: settings, sections, lessons (content items, quiz questions with '
        + 'answers), version, lineageId and assignmentCount. Read it before editing',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    create: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createTrainingTaskBodySchema,
      responses: {
        201: trainingTaskDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Create a course as a draft: title, category, description, sections (modules) and at '
        + 'least one lesson — video link (YouTube / Vimeo / MP4 URL), reading article (markdown), '
        + 'checklist, or quiz with questions — plus certificate and strict-mode settings. Publish '
        + 'it before assigning',
      metadata: routeMeta('learning:manage'),
    },
    patch: {
      method: 'PATCH',
      path: '/:taskId',
      headers: idempotencyHeadersSchema,
      body: patchTrainingTaskBodySchema,
      responses: {
        200: trainingTaskDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Edit a course: its settings, or its sections and lessons — sending items REPLACES the '
        + 'whole lesson list, so send every lesson to keep, in order (add, remove, reorder). A '
        + 'course that already has assignments returns a NEW draft version with a different id '
        + '(D1): use the id in the response from then on, and publish again',
      metadata: routeMeta('learning:manage'),
    },
    archive: {
      method: 'POST',
      path: '/:taskId/archive',
      body: z.object({}),
      responses: {
        200: archiveTrainingTaskResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Archive a course so it can no longer be assigned; existing assignments carry on',
      metadata: routeMeta('learning:manage'),
    },
    publish: {
      method: 'POST',
      path: '/:taskId/publish',
      body: z.object({}),
      responses: {
        200: publishTrainingTaskResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Publish a draft course (draft -> active) so it can be assigned. 412 when a quiz has no '
        + 'questions or pass mark, or the allotted time is below the content minimum',
      metadata: routeMeta('learning:manage'),
    },
    preview: {
      method: 'GET',
      path: '/:taskId/preview',
      responses: {
        200: previewTrainingTaskResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Preview as learner: items and quiz questions without `correct`. Creates no assignment, '
        + 'session, recording or progress; the web player runs in previewMode and never sends heartbeats',
      metadata: routeMeta('learning:manage', { ai: 'hidden' }),
    },
    contentUploadTicket: {
      method: 'POST',
      path: '/content/upload-ticket',
      body: trainingContentUploadTicketBodySchema,
      responses: {
        200: trainingContentUploadTicketResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Presigned PRIVATE PUT for a document (PDF/image, 10 MB) or a video (MP4/WebM, 500 MB)',
      metadata: routeMeta('learning:manage', { ai: 'hidden' }),
    },
    addLessonFromAttachment: {
      method: 'POST',
      path: '/:taskId/lessons/from-attachment',
      headers: idempotencyHeadersSchema,
      body: addTrainingLessonFromAttachmentBodySchema,
      responses: {
        200: trainingTaskDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Add a document lesson to a course from a PDF the person attached in this chat (by '
        + 'attachmentId). Returns the course — a NEW draft version (different id) when it already '
        + 'has assignments, which then needs publishing again',
      metadata: routeMeta('learning:manage'),
    },
  },
  { pathPrefix: '/training/tasks' },
);

export const trainingCategoriesContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      responses: { 200: z.array(trainingCategorySchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List course categories (training categories)',
      metadata: routeMeta('learning:read'),
    },
    create: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createTrainingCategoryBodySchema,
      responses: {
        201: trainingCategorySchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a course category',
      metadata: routeMeta('learning:manage'),
    },
    patch: {
      method: 'PATCH',
      path: '/:categoryId',
      body: patchTrainingCategoryBodySchema,
      responses: {
        200: trainingCategorySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename or reorder a course category',
      metadata: routeMeta('learning:manage'),
    },
  },
  { pathPrefix: '/training/categories' },
);

export const trainingBatchesContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingBatchesQuerySchema,
      responses: {
        200: paginatedSchema(trainingBatchSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Course assignment batches — each bulk assign, with its audience, counts and whether it '
        + 'still auto-enrols new joiners',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    get: {
      method: 'GET',
      path: '/:batchId',
      responses: {
        200: trainingBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One course assignment batch: its audience, counts and auto-enrol state',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    close: {
      method: 'POST',
      path: '/:batchId/close',
      body: z.object({}),
      responses: {
        200: trainingBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Stop a course assignment batch auto-enrolling new joiners (existing assignments stay)',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
  },
  { pathPrefix: '/training/batches' },
);

// ===================================================================================================
// Admin monitor and policy (plan §3.5)
// ===================================================================================================

export const trainingMonitorAttendanceStateSchema = z.enum([
  'clocked_in',
  'clock_out_blocked',
  'overrunning',
  'clocked_out',
  'not_clocked_in',
]);

export const trainingMonitorRowSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  todayProgressPercent: z.string(),
  currentTaskTitle: z.string().nullable(),
  currentDisplayStatus: trainingDisplayStatusSchema.nullable(),
  overdueCount: z.number().int(),
  verificationStatus: trainingVerificationStatusSchema.nullable(),
  attendanceState: trainingMonitorAttendanceStateSchema,
  lastHeartbeatAt: instantSchema.nullable(),
});
export type TrainingMonitorRow = z.infer<typeof trainingMonitorRowSchema>;

export const trainingMonitorKpisSchema = z.object({
  totalAssigned: z.number().int(),
  completedToday: z.number().int(),
  overdueCount: z.number().int(),
  blockingClockOutCount: z.number().int(),
});

export const trainingMonitorQuerySchema = paginationQuerySchema.extend({
  date: dateOnlySchema.optional(),
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  managerPersonId: uuidSchema.optional(),
  display: trainingDisplayStatusSchema.optional(),
});

export const trainingMonitorResponseSchema = paginatedSchema(trainingMonitorRowSchema).extend({
  kpis: trainingMonitorKpisSchema,
});

export const releaseTrainingClockOutBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export const releaseTrainingClockOutResponseSchema = z.object({
  releasedCount: z.number().int(),
  assignmentIds: z.array(uuidSchema),
});

export const trainingPolicySchema = z.object({
  legalEntityId: uuidSchema.nullable(),
  dueSoonLeadMinutes: z.number().int().positive(),
  maxOverrunMinutes: z.number().int().positive(),
  twoStepApprovalOverMinutes: z.number().int().positive(),
  requireClockedInToStart: z.boolean(),
  creditCutoffMode: z.enum(['exclude_overtime', 'exclude_worked_time']),
  updatedAt: instantSchema,
});
export type TrainingPolicy = z.infer<typeof trainingPolicySchema>;

export const updateTrainingPolicyBodySchema = trainingPolicySchema
  .omit({ legalEntityId: true, updatedAt: true })
  .partial();

export const trainingPolicyQuerySchema = z.object({ legalEntityId: uuidSchema.optional() });

export const trainingAdminContract = c.router(
  {
    monitor: {
      method: 'GET',
      path: '/monitor',
      query: trainingMonitorQuerySchema,
      responses: {
        200: trainingMonitorResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Training progress monitor: per-person course progress rows (scope-filtered) plus KPI '
        + 'totals',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    releaseClockOut: {
      method: 'POST',
      path: '/people/:personId/release-clock-out',
      headers: idempotencyHeadersSchema,
      body: releaseTrainingClockOutBodySchema,
      responses: {
        200: releaseTrainingClockOutResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Emergency release: closes a person's open blocking course assignments for today as "
        + 'incomplete (clock_out_released). A caller may not release their own',
      metadata: routeMeta('learning.override:execute'),
    },
    getPolicy: {
      method: 'GET',
      path: '/policy',
      query: trainingPolicyQuerySchema,
      responses: {
        200: trainingPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Read the training policy (LMS settings: due-soon lead time, maximum overrun, two-step '
        + 'approval threshold, credit cut-off) for a legal entity, or the org default',
      metadata: routeMeta('learning:manage'),
    },
    updatePolicy: {
      method: 'PUT',
      path: '/policy',
      headers: idempotencyHeadersSchema,
      query: trainingPolicyQuerySchema,
      body: updateTrainingPolicyBodySchema,
      responses: {
        200: trainingPolicySchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Update the training policy (LMS settings) for a legal entity, or the org default when '
        + 'legalEntityId is absent',
      metadata: routeMeta('learning:manage'),
    },
  },
  { pathPrefix: '/training/admin' },
);

// ===================================================================================================
// Certificates (Phase 2A, §3.7) — management + public verification
// ===================================================================================================

export const downloadTrainingCertificateResponseSchema = z.object({
  url: z.string(),
  expiresAt: instantSchema,
});
export const revokeTrainingCertificateBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export const verifyTrainingCertificateResponseSchema = z.object({
  valid: z.boolean(),
  holderName: z.string().nullable(),
  taskTitle: z.string().nullable(),
  issuedAt: instantSchema.nullable(),
  expiresAt: instantSchema.nullable(),
  revoked: z.boolean(),
});

export const trainingCertificatesContract = c.router(
  {
    download: {
      method: 'POST',
      path: '/:certificateId/download',
      body: z.object({}),
      responses: {
        200: downloadTrainingCertificateResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: '5-minute presigned GET for the certificate PDF — own, or learning:manage over the person',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
    revoke: {
      method: 'POST',
      path: '/:certificateId/revoke',
      headers: idempotencyHeadersSchema,
      body: revokeTrainingCertificateBodySchema,
      responses: {
        200: trainingCertificateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Revoke an issued course certificate and the skills it awarded, with a reason. Not on '
        + 'your own (learning.override:execute self-exclusion)',
      metadata: routeMeta('learning:manage'),
    },
  },
  { pathPrefix: '/training/certificates' },
);

/**
 * Genuinely public (`routeMeta('public')`, invariant 8) — a prospective employer or the person
 * themself checking a certificate holds no session. Rate-limiting is noted in the plan but is an
 * API-adapter concern, not a contract one; nothing here enforces it.
 */
export const publicCertificatesContract = c.router(
  {
    verify: {
      method: 'GET',
      path: '/verify/:verificationCode',
      responses: {
        200: verifyTrainingCertificateResponseSchema,
        404: errorBodySchema,
      },
      summary:
        'Public certificate verification — holder initial + surname only, no other PII. Rate-limited',
      metadata: routeMeta('public', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/public/certificates' },
);

// ===================================================================================================
// Learning analytics (Phase 2A, §3.7) — every row scope-filtered, every number a DB aggregate
// ===================================================================================================

const trainingAnalyticsScopeQuerySchema = z.object({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
});

export const trainingAnalyticsSummarySchema = z.object({
  assigned: z.number().int(),
  completed: z.number().int(),
  completionRate: z.number().min(0).max(100),
  onTimeRate: z.number().min(0).max(100),
  overdue: z.number().int(),
  overrunRate: z.number().min(0).max(100),
  avgOverrunMinutes: z.number(),
  quizPassRate: z.number().min(0).max(100),
  avgAttempts: z.number(),
  verifiedRate: z.number().min(0).max(100),
  activeLearners: z.number().int(),
});

export const trainingAnalyticsTrendQuerySchema = z.object({
  metric: z.enum(['completions', 'active_learners', 'overruns']),
  interval: z.enum(['week', 'month']),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export const trainingAnalyticsTrendResponseSchema = z.object({
  series: z.array(z.object({ periodStart: dateOnlySchema, value: z.number() })),
});

export const trainingAnalyticsCourseRowSchema = z.object({
  taskId: uuidSchema,
  title: z.string(),
  status: trainingTaskStatusSchema,
  isNew: z.boolean(),
  assigned: z.number().int(),
  completionRate: z.number().min(0).max(100),
  avgMinutesVsAllotted: z.number(),
  quizPassRate: z.number().min(0).max(100).nullable(),
  popularityRank: z.number().int().nullable(),
});
export const trainingAnalyticsCoursesResponseSchema = z.object({
  courses: z.array(trainingAnalyticsCourseRowSchema),
});

export const trainingAnalyticsBreakdownQuerySchema = trainingAnalyticsScopeQuerySchema.extend({
  by: z.enum(['department', 'location', 'manager', 'cohort']),
});
export const trainingAnalyticsBreakdownResponseSchema = z.object({
  rows: z.array(
    z.object({
      groupId: uuidSchema.nullable(),
      groupLabel: z.string(),
      assigned: z.number().int(),
      completionRate: z.number().min(0).max(100),
      overdue: z.number().int(),
      overrunRate: z.number().min(0).max(100),
      skillCoverageRate: z.number().min(0).max(100).nullable(),
    }),
  ),
});

export const trainingAnalyticsExportResponseSchema = z.object({
  downloadUrl: z.string(),
  expiresAt: instantSchema,
});

const trainingAnalyticsPermission = ['learning:manage', 'learning.assign:execute'] as const;

export const trainingAnalyticsContract = c.router(
  {
    summary: {
      method: 'GET',
      path: '/summary',
      query: trainingAnalyticsScopeQuerySchema,
      responses: { 200: trainingAnalyticsSummarySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Training (LMS) KPIs for a date range: assigned, completed, completion and on-time '
        + 'rates, overdue, quiz pass rate, active learners; filter by legal entity, department or '
        + 'category',
      metadata: routeMeta(trainingAnalyticsPermission),
    },
    trend: {
      method: 'GET',
      path: '/trend',
      query: trainingAnalyticsTrendQuerySchema,
      responses: { 200: trainingAnalyticsTrendResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Training trend over time, by week or month: completions, active learners or overruns',
      metadata: routeMeta(trainingAnalyticsPermission),
    },
    courses: {
      method: 'GET',
      path: '/courses',
      query: trainingAnalyticsScopeQuerySchema,
      responses: { 200: trainingAnalyticsCoursesResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Per-course training report: status, completion, timing and quiz stats, with popularity '
        + 'rank',
      metadata: routeMeta(trainingAnalyticsPermission),
    },
    breakdown: {
      method: 'GET',
      path: '/breakdown',
      query: trainingAnalyticsBreakdownQuerySchema,
      responses: { 200: trainingAnalyticsBreakdownResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Training completion, overdue, overrun and skill coverage broken down by department, '
        + 'location, manager or joining cohort',
      metadata: routeMeta(trainingAnalyticsPermission),
    },
    export: {
      method: 'GET',
      path: '/export',
      query: trainingAnalyticsScopeQuerySchema,
      responses: { 200: trainingAnalyticsExportResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'CSV export — an idempotent download ticket, not the file itself',
      metadata: routeMeta(trainingAnalyticsPermission, { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/analytics' },
);

// ===================================================================================================
// Training redesign (docs/plans/training-redesign) — new routers. Additive: nothing above changes
// meaning. `trainingLearner` is the learner side; `trainingCourses` + `trainingLegacyAssignments`
// are the HR side.
// ===================================================================================================

/** The lesson "Continue" opens — what the hero card on My learning names. */
export const trainingNextLessonSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  kind: trainingContentKindSchema,
  durationSeconds: z.number().int().nullable(),
  sectionTitle: z.string().nullable(),
  // ---- Several languages (additive; absent = English only) ----
  /** So the card names the lesson in the learner's language, like the course title beside it. */
  language: trainingLanguageSchema.optional(),
  translations: trainingTextTranslationsSchema.optional(),
});

export const trainingLearnerHomeSchema = z.object({
  /** The open course touched most recently (else the open course due soonest), or null. */
  continueLearning: z
    .object({ assignment: trainingAssignmentSchema, nextLesson: trainingNextLessonSchema.nullable() })
    .nullable(),
  /** Every non-cancelled course assigned to the caller — the In progress / Not started / Completed
   * tabs split this client-side with `trainingLearnerView()`. */
  assignments: z.array(trainingAssignmentSchema),
  /** Open items from the older "Learning & Training" screen (redesign §9.2) — still the caller's
   * to finish (`learningAssignments.complete`), and still counted by the clock-out gate. */
  otherItems: z.array(learningAssignmentSchema),
  certificateCount: z.number().int(),
});
export type TrainingLearnerHome = z.infer<typeof trainingLearnerHomeSchema>;

export const trainingTeamQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  /** Only people with at least one overdue course. */
  overdueOnly: queryBooleanSchema.optional(),
});

export const trainingTeamCourseSchema = z.object({
  assignmentId: uuidSchema,
  /** Lineage id — stable across course versions. */
  courseId: uuidSchema,
  title: z.string(),
  learnerStatus: trainingLearnerStatusSchema,
  overlay: trainingDueOverlaySchema,
  progressPercent: z.string(),
  dueAt: instantSchema.nullable(),
  lastActivityAt: instantSchema.nullable(),
});

export const trainingTeamMemberSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  departmentName: z.string().nullable(),
  assigned: z.number().int(),
  completed: z.number().int(),
  overdue: z.number().int(),
  courses: z.array(trainingTeamCourseSchema),
});
export type TrainingTeamMember = z.infer<typeof trainingTeamMemberSchema>;

export const trainingLearnerContract = c.router(
  {
    home: {
      method: 'GET',
      path: '/home',
      responses: { 200: trainingLearnerHomeSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        "My learning: the caller's courses, the one to continue and its next lesson, open older "
        + 'learning items, and the certificate count',
      metadata: routeMeta('learning:read'),
    },
    team: {
      method: 'GET',
      path: '/team',
      query: trainingTeamQuerySchema,
      responses: {
        200: paginatedSchema(trainingTeamMemberSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "My team's learning: each person the caller may assign courses to (a manager's "
        + 'reporting tree) with their courses, progress and overdue count',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
  },
  { pathPrefix: '/training/learner' },
);

// ---- Course page (HR): learners, reminders, embeds, strict-mode review ---------------------------

export const trainingCourseLearnersQuerySchema = paginationQuerySchema.extend({
  status: trainingLearnerStatusSchema.optional(),
  overlay: z.enum(['overdue', 'due_soon']).optional(),
  departmentId: uuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
});

export const trainingCourseLearnerRowSchema = z.object({
  assignmentId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  departmentName: z.string().nullable(),
  /** The course version this person is on (D1 — they finish the version they started). */
  version: z.number().int(),
  assignedOn: dateOnlySchema,
  dueAt: instantSchema.nullable(),
  isMandatory: z.boolean(),
  status: trainingAssignmentStatusSchema,
  /** `null` for a cancelled assignment ("Removed"). */
  learnerStatus: trainingLearnerStatusSchema.nullable(),
  overlay: trainingDueOverlaySchema,
  late: z.boolean(),
  progressPercent: z.string(),
  lessonsCompleted: z.number().int(),
  lessonsTotal: z.number().int(),
  lastActivityAt: instantSchema.nullable(),
  completedAt: instantSchema.nullable(),
  lastRemindedAt: instantSchema.nullable(),
  certificateId: uuidSchema.nullable(),
});
export type TrainingCourseLearnerRow = z.infer<typeof trainingCourseLearnerRowSchema>;

/** Whole-course totals for the strip above the table — DB aggregates over the caller's scope,
 * independent of the page and the filters. */
export const trainingCourseLearnerCountsSchema = z.object({
  assigned: z.number().int(),
  notStarted: z.number().int(),
  inProgress: z.number().int(),
  completed: z.number().int(),
  notCompleted: z.number().int(),
  overdue: z.number().int(),
});

export const trainingCourseLearnersResponseSchema = paginatedSchema(
  trainingCourseLearnerRowSchema,
).extend({
  counts: trainingCourseLearnerCountsSchema,
});
export type TrainingCourseLearnersResponse = z.infer<typeof trainingCourseLearnersResponseSchema>;

/** At most one reminder per assignment per this many hours; the rest come back as skipped. */
export const TRAINING_REMINDER_MIN_INTERVAL_HOURS = 24;

export const remindTrainingAssignmentsBodySchema = z.object({
  assignmentIds: z.array(uuidSchema).min(1).max(200),
});
export const remindTrainingAssignmentsResponseSchema = z.object({
  sent: z.number().int(),
  skipped: z.array(
    z.object({
      assignmentId: uuidSchema,
      reason: z.enum(['reminded_recently', 'not_open', 'out_of_scope']),
    }),
  ),
});

export const trainingEmbedLookupBodySchema = z.object({ url: z.string().url() });
export const trainingEmbedLookupResponseSchema = z.object({
  provider: trainingEmbedProviderSchema,
  embedId: z.string(),
  /** From the provider's public oEmbed endpoint — `null` when it is unreachable or private. */
  title: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  /** Vimeo's oEmbed reports it; YouTube's does not — the builder reads it from the player then. */
  durationSeconds: z.number().int().nullable(),
});

export const trainingStrictReviewSchema = z.object({
  /** Courses (latest version per lineage, not archived) with any strict-mode switch on. */
  strictCourseCount: z.number().int(),
  courses: z.array(
    z.object({
      /** Lineage id. */
      courseId: uuidSchema,
      /** Latest version. */
      taskId: uuidSchema,
      title: z.string(),
      status: trainingTaskStatusSchema,
      requiresWebcam: z.boolean(),
      blocksClockOut: z.boolean(),
      strictPlayback: z.boolean(),
      /** YouTube/Vimeo lessons on a strict course — they must become uploads for it to stay strict. */
      embedLessonCount: z.number().int(),
    }),
  ),
});

export const trainingCoursesContract = c.router(
  {
    // Literal paths first, so they are never read as a `:courseId`.
    remind: {
      method: 'POST',
      path: '/reminders',
      headers: idempotencyHeadersSchema,
      body: remindTrainingAssignmentsBodySchema,
      responses: {
        200: remindTrainingAssignmentsResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Remind learners about their open course assignments (in-app + email), by assignmentIds '
        + "from the course's learners; at most one per assignment per day, people beyond the "
        + "caller's scope skipped",
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
    embedLookup: {
      method: 'POST',
      path: '/embed-lookup',
      body: trainingEmbedLookupBodySchema,
      responses: {
        200: trainingEmbedLookupResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Recognise a YouTube/Vimeo link and fetch its title and thumbnail (and Vimeo duration) for '
        + 'the lesson editor. 400 when the link is not a supported video',
      metadata: routeMeta('learning:manage', { ai: 'hidden' }),
    },
    strictReview: {
      method: 'GET',
      path: '/strict-review',
      responses: { 200: trainingStrictReviewSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Courses using strict mode (webcam / clock-out lock / strict playback), for the '
        + 'one-time review notice',
      metadata: routeMeta('learning:manage'),
    },
    learners: {
      method: 'GET',
      path: '/:courseId/learners',
      query: trainingCourseLearnersQuerySchema,
      responses: {
        200: trainingCourseLearnersResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "A course's learners: who has it (any version), with assignmentId, progress, status and "
        + "due date, plus totals — the course page's Learners tab. courseId is the course's "
        + 'lineageId (from trainingTasks list/get), not its version id',
      metadata: routeMeta(['learning:manage', 'learning.assign:execute']),
    },
  },
  { pathPrefix: '/training/courses' },
);

// ---- Older "Learning & Training" items (redesign §9.2): HR's list and "Turn into a course" ------

const trainingLegacyStatusSchema = z.enum(['assigned', 'in_progress', 'completed', 'waived', 'converted']);

export const listTrainingLegacyAssignmentsQuerySchema = paginationQuerySchema.extend({
  status: trainingLegacyStatusSchema.optional(),
  /** Exact title — the rows of one group. */
  title: z.string().trim().max(200).optional(),
  q: z.string().trim().max(200).optional(),
});

export const trainingLegacyAssignmentRowSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  title: z.string(),
  dueDate: dateOnlySchema.nullable(),
  isMandatory: z.boolean(),
  status: trainingLegacyStatusSchema,
  completedAt: instantSchema.nullable(),
  convertedToAssignmentId: uuidSchema.nullable(),
  createdAt: instantSchema,
});

/** One row per distinct title — what HR converts as a unit. */
export const trainingLegacyGroupSchema = z.object({
  title: z.string(),
  description: z.string().nullable(),
  open: z.number().int(),
  completed: z.number().int(),
  waived: z.number().int(),
  converted: z.number().int(),
  mandatoryOpen: z.number().int(),
  earliestDueDate: dateOnlySchema.nullable(),
});

export const convertTrainingLegacyBodySchema = z.object({
  /** The group to convert: every OPEN item with exactly this title. */
  sourceTitle: z.string().trim().min(1).max(200),
  /** Defaults to `sourceTitle`. */
  courseTitle: z.string().trim().min(1).max(200).optional(),
  category: z.string().trim().min(1).max(100),
  categoryId: uuidSchema.optional(),
  due: trainingAssignmentDueSchema,
  /** Omitted → each new assignment keeps its old item's mandatory flag. */
  isMandatory: z.boolean().optional(),
});
export const convertTrainingLegacyResponseSchema = z.object({
  taskId: uuidSchema,
  /** Lineage id. */
  courseId: uuidSchema,
  /** New course assignments — one per open item's person. */
  assignedCount: z.number().int(),
  /** Old items closed as `converted` — only those whose person now has the course. */
  convertedCount: z.number().int(),
  skipped: z.array(z.object({ itemId: uuidSchema, reason: trainingAssignmentSkipReasonSchema })),
});

export const trainingLegacyAssignmentsContract = c.router(
  {
    groups: {
      method: 'GET',
      path: '/groups',
      responses: {
        200: z.array(trainingLegacyGroupSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Older "Learning & Training" items (pre-LMS to-dos, not courses) grouped by title, with '
        + 'open/done/waived/converted counts',
      metadata: routeMeta('learning:manage'),
    },
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingLegacyAssignmentsQuerySchema,
      responses: {
        200: paginatedSchema(trainingLegacyAssignmentRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Older "Learning & Training" items (pre-LMS to-dos, not courses), one row per person',
      metadata: routeMeta('learning:manage'),
    },
    convert: {
      method: 'POST',
      path: '/convert',
      headers: idempotencyHeadersSchema,
      body: convertTrainingLegacyBodySchema,
      responses: {
        201: convertTrainingLegacyResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Turn a group of open older learning items into a published one-lesson course, assign '
        + 'it to those people and close each old item as converted, in one transaction',
      metadata: routeMeta('learning:manage'),
    },
  },
  { pathPrefix: '/training/legacy-assignments' },
);

// ===================================================================================================
// Certificates (training redesign PLAN §13): company designs, reissue + history, HR's list of every
// issued certificate, and external certificates HR uploads for a person.
// ===================================================================================================

export const trainingCertificateLayoutSchema = z.enum(TRAINING_CERTIFICATE_LAYOUTS);
export const trainingCertificateOrientationSchema = z.enum(TRAINING_CERTIFICATE_ORIENTATIONS);
export const trainingCertificateAccentSchema = z.enum(TRAINING_CERTIFICATE_ACCENTS);
export const trainingCertificateLogoSourceSchema = z.enum(TRAINING_CERTIFICATE_LOGO_SOURCES);
export const trainingCertificateValiditySchema = z.enum(TRAINING_CERTIFICATE_VALIDITIES);

const certificatePercentSchema = z.number().min(0).max(100);

/** A text placeholder's box on a custom design, in percent of the page (top-left corner + width). */
export const trainingCertificateTextFieldSchema = z.object({
  x: certificatePercentSchema,
  y: certificatePercentSchema,
  width: z.number().min(5).max(100),
  fontSize: z.number().int().min(8).max(96),
  align: z.enum(['left', 'center', 'right']),
  color: z.enum(['dark', 'light', 'accent']),
});

/** Placement of every field on a `custom_background` design. Only `name` is mandatory — the point of
 * a certificate; the rest are placed when HR wants them printed. The API clamps every box onto the
 * page (`clampTrainingCertificateFields`). */
export const trainingCertificateFieldsSchema = z.object({
  name: trainingCertificateTextFieldSchema,
  course: trainingCertificateTextFieldSchema.optional(),
  issuedOn: trainingCertificateTextFieldSchema.optional(),
  expiresOn: trainingCertificateTextFieldSchema.optional(),
  certificateNo: trainingCertificateTextFieldSchema.optional(),
  qr: z
    .object({ x: certificatePercentSchema, y: certificatePercentSchema, size: z.number().min(5).max(40) })
    .optional(),
  signatures: z
    .array(z.object({ x: certificatePercentSchema, y: certificatePercentSchema, width: z.number().min(5).max(60) }))
    .max(2)
    .optional(),
});
export type TrainingCertificateFieldsInput = z.infer<typeof trainingCertificateFieldsSchema>;

export const trainingCertificateSignatoryInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  designation: z.string().trim().max(100).default(''),
  /** From `contentUploadTicket` with `kind: 'certificate_asset'`; null → no signature image. */
  signatureKey: z.string().min(1).nullable().optional(),
});

export const trainingCertificateTemplateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  isDefault: z.boolean(),
  layout: trainingCertificateLayoutSchema,
  orientation: trainingCertificateOrientationSchema,
  heading: z.string(),
  statementBefore: z.string(),
  statementAfter: z.string(),
  accent: trainingCertificateAccentSchema,
  logoSource: trainingCertificateLogoSourceSchema,
  logoKey: z.string().nullable(),
  /** Short-lived GET for the custom logo (null unless `logoSource = custom`). */
  logoUrl: z.string().nullable(),
  backgroundKey: z.string().nullable(),
  backgroundUrl: z.string().nullable(),
  signatories: z.array(
    z.object({
      name: z.string(),
      designation: z.string(),
      signatureKey: z.string().nullable(),
      signatureUrl: z.string().nullable(),
    }),
  ),
  fields: trainingCertificateFieldsSchema.nullable(),
  showQr: z.boolean(),
  archivedAt: instantSchema.nullable(),
  /** Courses (latest versions, not archived) that print with this design. */
  courseCount: z.number().int(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TrainingCertificateTemplate = z.infer<typeof trainingCertificateTemplateSchema>;

export const saveTrainingCertificateTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  isDefault: z.boolean().optional(),
  layout: trainingCertificateLayoutSchema,
  orientation: trainingCertificateOrientationSchema,
  heading: z.string().trim().min(1).max(120),
  statementBefore: z.string().trim().max(200),
  statementAfter: z.string().trim().max(200),
  accent: trainingCertificateAccentSchema,
  logoSource: trainingCertificateLogoSourceSchema,
  /** Required when `logoSource = custom` (checked by the API). */
  logoKey: z.string().min(1).nullable().optional(),
  /** Required when `layout = custom_background` (checked by the API). */
  backgroundKey: z.string().min(1).nullable().optional(),
  /** Empty → the learner's legal entity signatory (Settings › Company), without a signature image. */
  signatories: z.array(trainingCertificateSignatoryInputSchema).max(2),
  /** Required when `layout = custom_background`. */
  fields: trainingCertificateFieldsSchema.nullable().optional(),
  showQr: z.boolean(),
});
export const patchTrainingCertificateTemplateBodySchema = saveTrainingCertificateTemplateBodySchema.partial();

export const listTrainingCertificateTemplatesQuerySchema = z.object({
  includeArchived: queryBooleanSchema.optional(),
});

export const trainingCertificateSampleResponseSchema = z.object({
  url: z.string(),
  expiresAt: instantSchema,
  /** `html` when the server cannot run Chrome (the PDF renderer's fallback). */
  format: z.enum(['pdf', 'html']),
});

export const trainingCertificateTemplatesContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingCertificateTemplatesQuerySchema,
      responses: { 200: z.array(trainingCertificateTemplateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Certificate designs (templates) for course certificates, default first',
      metadata: routeMeta('learning:manage'),
    },
    get: {
      method: 'GET',
      path: '/:templateId',
      responses: { 200: trainingCertificateTemplateSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'One certificate design (template)',
      metadata: routeMeta('learning:manage'),
    },
    create: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: saveTrainingCertificateTemplateBodySchema,
      responses: { 201: trainingCertificateTemplateSchema, 400: errorBodySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary:
        'Create a certificate design (template) for course certificates. The first one becomes '
        + 'the default',
      metadata: routeMeta('learning:manage'),
    },
    update: {
      method: 'PATCH',
      path: '/:templateId',
      headers: idempotencyHeadersSchema,
      body: patchTrainingCertificateTemplateBodySchema,
      responses: {
        200: trainingCertificateTemplateSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Edit a certificate design (template). Applies to certificates issued from now on; '
        + 'issued PDFs never change',
      metadata: routeMeta('learning:manage'),
    },
    setDefault: {
      method: 'POST',
      path: '/:templateId/default',
      body: z.object({}),
      responses: { 200: trainingCertificateTemplateSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      summary: 'Make a certificate design the company default (not an archived one)',
      metadata: routeMeta('learning:manage'),
    },
    archive: {
      method: 'POST',
      path: '/:templateId/archive',
      body: z.object({}),
      responses: { 200: trainingCertificateTemplateSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      summary:
        'Archive a certificate design; courses using it fall back to the default. 409 when it '
        + 'is the default while other active designs exist (set a new default first)',
      metadata: routeMeta('learning:manage'),
    },
    sample: {
      method: 'POST',
      path: '/:templateId/sample',
      body: z.object({}),
      responses: { 200: trainingCertificateSampleResponseSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Render a sample certificate with placeholder data — a short-lived link',
      metadata: routeMeta('learning:manage', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/certificate-templates' },
);

// ---- Issued course certificates: HR's list, reissue and history ------------------------------------

export const listTrainingIssuedCertificatesQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  validity: trainingCertificateValiditySchema.optional(),
  /** Course lineage id. */
  courseId: uuidSchema.optional(),
  personId: uuidSchema.optional(),
});

export const trainingIssuedCertificateRowSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  /** The name printed (snapshot), falling back to the profile name for older certificates. */
  holderName: z.string(),
  courseTitle: z.string(),
  courseId: uuidSchema,
  certificateNo: z.string(),
  issuedAt: instantSchema,
  expiresAt: instantSchema.nullable(),
  validity: trainingCertificateValiditySchema,
  revision: z.number().int(),
  status: trainingCertificateStatusSchema,
});
export type TrainingIssuedCertificateRow = z.infer<typeof trainingIssuedCertificateRowSchema>;

export const reissueTrainingCertificateBodySchema = z.object({
  /** The name to print — prefilled by the UI from the profile, editable ("Dr. Priya Sharma"). */
  holderName: z.string().trim().min(1).max(120),
  reason: z.string().trim().min(3).max(500),
  /** A design to use; omitted → the course's current design (or the company default). */
  templateId: uuidSchema.nullable().optional(),
});

export const trainingCertificateRevisionSchema = z.object({
  revision: z.number().int(),
  holderName: z.string().nullable(),
  templateName: z.string().nullable(),
  reason: z.string(),
  createdByName: z.string(),
  createdAt: instantSchema,
});

export const trainingCertificateIssuesContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingIssuedCertificatesQuerySchema,
      responses: {
        200: paginatedSchema(trainingIssuedCertificateRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Course certificates issued to people, scope-filtered per person (Reports › '
        + 'Certificates)',
      metadata: routeMeta('learning:manage'),
    },
    reissue: {
      method: 'POST',
      path: '/:certificateId/reissue',
      headers: idempotencyHeadersSchema,
      body: reissueTrainingCertificateBodySchema,
      responses: {
        200: trainingCertificateSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Reissue a course certificate with a corrected name and the current design. Same number '
        + 'and verification code; the previous file is kept as a revision. 409 when revoked. Not '
        + 'on your own',
      metadata: routeMeta('learning:manage'),
    },
    revisions: {
      method: 'GET',
      path: '/:certificateId/revisions',
      responses: {
        200: z.array(trainingCertificateRevisionSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A certificate's earlier versions (who reissued it, when, why, with what name)",
      metadata: routeMeta('learning:manage'),
    },
    revisionDownload: {
      method: 'POST',
      path: '/:certificateId/revisions/:revision/download',
      body: z.object({}),
      responses: {
        200: downloadTrainingCertificateResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'A short-lived link to an earlier version of a certificate',
      metadata: routeMeta('learning:manage', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/certificates' },
);

// ---- External certificates HR uploads for a person ------------------------------------------------

export const trainingExternalCertificateSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  title: z.string(),
  issuer: z.string(),
  issuedOn: dateOnlySchema,
  expiresOn: dateOnlySchema.nullable(),
  credentialId: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  notes: z.string().nullable(),
  validity: trainingCertificateValiditySchema,
  addedByName: z.string(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TrainingExternalCertificate = z.infer<typeof trainingExternalCertificateSchema>;

export const listTrainingExternalCertificatesQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
  validity: trainingCertificateValiditySchema.optional(),
});

const externalCertificateFieldsSchema = z.object({
  title: z.string().trim().min(1).max(200),
  issuer: z.string().trim().min(1).max(200),
  issuedOn: dateOnlySchema,
  /** The last valid day; must not be before `issuedOn` (checked by the API). */
  expiresOn: dateOnlySchema.nullable().optional(),
  credentialId: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});

export const createTrainingExternalCertificateBodySchema = externalCertificateFieldsSchema.extend({
  personId: uuidSchema,
  /** From `contentUploadTicket` with `kind: 'external_certificate'`. */
  storageKey: z.string().min(1),
  mimeType: z.enum(TRAINING_EXTERNAL_CERTIFICATE_MIME_TYPES),
  fileName: z.string().trim().min(1).max(255),
});

/** Any field may change; a new file replaces the old one (send all three file fields together). */
export const patchTrainingExternalCertificateBodySchema = externalCertificateFieldsSchema.partial().extend({
  storageKey: z.string().min(1).optional(),
  mimeType: z.enum(TRAINING_EXTERNAL_CERTIFICATE_MIME_TYPES).optional(),
  fileName: z.string().trim().min(1).max(255).optional(),
});

export const deleteTrainingExternalCertificateBodySchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

export const trainingExternalCertificatesContract = c.router(
  {
    // Literal path first so it is never read as an `:certificateId`.
    mine: {
      method: 'GET',
      path: '/mine',
      responses: { 200: z.array(trainingExternalCertificateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: "The caller's own external certificates (earned outside the company)",
      metadata: routeMeta('learning:read'),
    },
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingExternalCertificatesQuerySchema,
      responses: {
        200: paginatedSchema(trainingExternalCertificateSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'External certificates (earned outside the company) recorded for people, scope-filtered '
        + '(Reports › Certificates)',
      metadata: routeMeta('learning:manage'),
    },
    create: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createTrainingExternalCertificateBodySchema,
      responses: {
        201: trainingExternalCertificateSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Record an external certificate (earned outside the company) for a person. Needs an '
        + "uploaded file's storageKey, which only the web screen can produce",
      metadata: routeMeta('learning:manage'),
    },
    update: {
      method: 'PATCH',
      path: '/:certificateId',
      headers: idempotencyHeadersSchema,
      body: patchTrainingExternalCertificateBodySchema,
      responses: {
        200: trainingExternalCertificateSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Correct the details or replace the file of an external certificate',
      metadata: routeMeta('learning:manage'),
    },
    remove: {
      method: 'POST',
      path: '/:certificateId/delete',
      body: deleteTrainingExternalCertificateBodySchema,
      responses: { 200: z.object({ deleted: z.literal(true) }), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Remove an external certificate (kept in the audit trail, hidden everywhere else)',
      metadata: routeMeta('learning:manage'),
    },
    download: {
      method: 'POST',
      path: '/:certificateId/download',
      body: z.object({}),
      responses: { 200: downloadTrainingCertificateResponseSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'A short-lived link to the file — the person themself, or learning:manage over them',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/training/external-certificates' },
);
